// Chatbot.js

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Chatbot.css'; // Import the CSS file
import { BeatLoader } from 'react-spinners'; // Import the spinner

const Chatbot = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ top: 20, left: 20 });
    const [isMinimized, setIsMinimized] = useState(true);
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
    const [loading, setLoading] = useState(false); // Loading state
    const [touchCoords, setTouchCoords] = useState({ x: 0, y: 0 }); // State for touch coordinates
    const chatEndRef = useRef(null);
    const initialPosition = useRef({ top: 0, left: 0, mouseX: 0, mouseY: 0 });

    useEffect(() => {
        const handleResize = () => {
            setViewportWidth(window.innerWidth);
            setViewportHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const setInitialPosition = () => {
            const chatbotWidth = 400;
            const chatbotHeight = 400;
            const bottomOffset = 20;
            const rightOffset = 20;

            setPosition({
                top: window.innerHeight - chatbotHeight - bottomOffset,
                left: window.innerWidth - chatbotWidth - rightOffset,
            });
        };

        setInitialPosition();
    }, []);

    const handleSend = async () => {
        if (!input) return;

        const userMessage = { sender: 'user', text: input };
        setMessages((prevMessages) => [...prevMessages, userMessage]);
        setLoading(true); // Set loading to true when sending a message
        
        try {
            // const response = await axios.post('http://101.100.163.102:8529/chat', {
            // const response = await axios.post('https://services.deliveranz.com.sg:8529/chat', {
            const response = await axios.post('/.netlify/functions/proxy', {
                message: input,
            });

            const botMessage = { sender: 'bot', text: response.data.response };
            setMessages((prevMessages) => [...prevMessages, botMessage]);
        } catch (error) {
            const errorMessage = { sender: 'bot', text: 'Sorry, something went wrong.' };
            setMessages((prevMessages) => [...prevMessages, errorMessage]);
        } finally {
            setLoading(false); // Set loading to false after receiving a response
        }

        setInput('');
    };

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const handleMouseDown = (e) => {
        if (viewportWidth < 400) return; // Prevent dragging if viewport is less than 400
        setIsDragging(true);
        initialPosition.current = {
            top: position.top,
            left: position.left,
            mouseX: e.clientX || e.touches[0].clientX,
            mouseY: e.clientY  || e.touches[0].clientY,
        };
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;

        // Determine if the event is a touch event or a mouse event
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        const deltaX = clientX - initialPosition.current.mouseX;
        const deltaY = clientY - initialPosition.current.mouseY;

        setPosition({
            top: initialPosition.current.top + deltaY,
            left: initialPosition.current.left + deltaX,
        });

        // Update touch coordinates for debugging
        setTouchCoords({ x: clientX.toFixed(0), y: clientY.toFixed(0) });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('touchmove', handleMouseMove); // Add touch move event
        window.addEventListener('touchend', handleMouseUp); // Add touch end event

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleMouseMove); // Clean up touch move event
            window.removeEventListener('touchend', handleMouseUp); // Clean up touch end event

        };
    }, [isDragging]);

    const toggleMinimize = () => {
        setIsMinimized(!isMinimized);
    };

    const isMobileView = viewportWidth < 400;

    // Adjust position when the input is focused or blurred
    const handleFocus = () => {
        if (isMobileView) {
            setPosition({ top: viewportHeight - 20 - 200, left: 0 }); // Adjust position to avoid keyboard
        }
    };

    const handleBlur = () => {
        if (isMobileView) {
            // Reset position when the keyboard is hidden
            setPosition({
                top: viewportHeight - 400 - 20, // Reset to original position
                left: 0,
            });
        }
    };

    // Calculate the chatbot's position based on viewport height
    const calculateChatbotPosition = () => {
        if (isMobileView) {
            return {
                position: 'fixed',
                bottom: '0',
                left: '0',
                width: '100vw',
                height: '30vh',
            };
        } else {
            return {
                position: 'absolute',
                top: `${position.top}px`,
                left: `${position.left}px`,
            };
        }
    };

    const chatbotStyle = calculateChatbotPosition();

    return (
        <>
            {!isMinimized ? (
                <div
                    className="chatbot"
                    style={chatbotStyle}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleMouseDown} // Add touch start event
                >
                    <div className="chatbot-header">
                        <span>

                        <img 
                            // src="./rickshaw.png" 
                            src="./profile.png" 
                            alt="Chat Icon" 
                            style={{ width: '35px', height: '35px', borderRadius: '50%', backgroundColor: 'white' }} // Adjust size as needed
                        /></span>Ask me about business automation!
                        {/* Touch Coordinates: X: {touchCoords.x}, Y: {touchCoords.y} */}
                        
                        <button
                            className="btn btn-link text-white"
                            onClick={toggleMinimize}
                            style={{ fontSize: '24px', fontWeight: 'bold', textDecoration: 'none' }} // Remove underline
                        >
                            — {/* Thick hyphen for minimize button */}
                        </button>
                    </div>
                    <div className="chatbot-messages">
                        {messages.map((msg, index) => (
                            <div
                                key={index}
                                className={`message mb-2 p-2 rounded ${
                                    msg.sender === 'user' ? 'bg-dark text-white' : 'bg-secondary text-white'
                                }`}
                                style={{
                                    maxWidth: '70%',
                                    wordWrap: 'break-word',
                                    alignSelf: msg.sender === 'user' ? 'flex-start' : 'flex-end', // Align based on sender
                                    marginLeft: msg.sender === 'user' ? '0' : 'auto', // Align to right for bot messages
                                    marginRight: msg.sender === 'user' ? 'auto' : '0', // Align to left for user messages 
                                }}
                            >
                                {msg.text}
                            </div>
                        ))}
                        {loading && ( // Show spinner when loading
                            <div className="d-flex justify-content-end mb-2">
                                <BeatLoader color="#343a40" loading={loading} size={15} />
                            </div>

                        )}
                        <div ref={chatEndRef} />
                    </div>
                    <div className="chatbot-input">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                            placeholder="Type your message..."
                            className="form-control me-2"
                            onFocus={handleFocus} // Add focus event handler
                            onBlur={handleBlur}   // Add blur event handler
                        />
                        <button onClick={handleSend} className="bg-dark btn btn-primary">
                            Send
                        </button>
                    </div>
                </div>
            ) : (
                <div
                    className="chatbot-minimized"
                    onClick={toggleMinimize}
                    style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '20px',
                        cursor: 'pointer',
                    }}
                >
                    <img 
                        src="./rickshaw.png" 
                        alt="Chat Icon" 
                        style={{ width: '45px', height: '45px' }} // Adjust size as needed
                    />
                </div>
            )}
        </>
    );
};

export default Chatbot;
